import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConsultationHeader = ({ 
  patient, 
  appointmentTime, 
  consultationType, 
  isRecording, 
  onToggleRecording,
  onEndConsultation 
}) => {
  const navigate = useNavigate();

  const formatAppointmentTime = (date) => {
    return date?.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getConsultationTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'routine': return 'bg-green-100 text-green-800';
      case 'follow-up': return 'bg-blue-100 text-blue-800';
      case 'urgent': return 'bg-orange-100 text-orange-800';
      case 'emergency': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleEndConsultation = () => {
    if (window.confirm('Are you sure you want to end this consultation?')) {
      onEndConsultation();
      navigate('/provider-dashboard');
    }
  };

  return (
    <div className="bg-card border-b border-border px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Left Section - Patient Info */}
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/provider-dashboard')}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="ArrowLeft" size={20} />
          </Button>
          
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <Icon name="User" size={20} color="white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">
                Video Consultation with {patient?.name}
              </h1>
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" size={14} />
                  <span>{formatAppointmentTime(appointmentTime)}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={14} />
                  <span>30 min appointment</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Center Section - Consultation Type */}
        <div className="flex items-center space-x-3">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getConsultationTypeColor(consultationType)}`}>
            {consultationType} Consultation
          </span>
          
          {isRecording && (
            <div className="flex items-center space-x-2 bg-red-50 text-red-700 px-3 py-1 rounded-full">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-xs font-medium">Recording</span>
            </div>
          )}
        </div>

        {/* Right Section - Actions */}
        <div className="flex items-center space-x-3">
          <Button
            variant={isRecording ? "destructive" : "outline"}
            size="sm"
            onClick={onToggleRecording}
            iconName={isRecording ? "Square" : "Circle"}
            iconPosition="left"
          >
            {isRecording ? 'Stop Recording' : 'Start Recording'}
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/patient-records')}
            iconName="FileText"
            iconPosition="left"
          >
            Patient Records
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/prescription-management')}
            iconName="Pill"
            iconPosition="left"
          >
            Prescriptions
          </Button>

          <div className="h-6 w-px bg-border" />

          <Button
            variant="destructive"
            size="sm"
            onClick={handleEndConsultation}
            iconName="PhoneOff"
            iconPosition="left"
          >
            End Consultation
          </Button>
        </div>
      </div>
      {/* Quick Stats Bar */}
      <div className="mt-4 flex items-center space-x-6 text-sm text-muted-foreground">
        <div className="flex items-center space-x-2">
          <Icon name="Users" size={14} />
          <span>Patient ID: {patient?.id}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="MapPin" size={14} />
          <span>Location: {patient?.location}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Shield" size={14} />
          <span>Insurance: {patient?.insurance}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Phone" size={14} />
          <span>Emergency Contact: {patient?.emergencyContact}</span>
        </div>
      </div>
    </div>
  );
};

export default ConsultationHeader;